import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { spaceTemplatesService } from "@/services/spaceTemplatesService";
import { useAuth } from "@/contexts/AuthContext";
import { 
  Plus, Trash2, Save, Loader2, Sparkles, FolderPlus, Bot, MessageSquare,
  Palette, X, GripVertical
} from "lucide-react";
import { TEMPLATE_CATEGORIES } from "@/data/spaceTemplates";
import type { SpaceTemplateData, SpaceTemplateTopicConfig, SpaceTemplateAgentConfig, SpaceTemplatePromptConfig } from "@shared/schema";

interface TemplateBuilderProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingTemplate?: {
    id: string;
    name: string;
    description: string;
    category: string;
    icon: string;
    color: string;
    tagline: string;
    templateData: SpaceTemplateData;
  };
}

const ICON_OPTIONS = [
  "Sparkles", "BookOpen", "Briefcase", "GraduationCap", "Heart", "Palette",
  "Target", "Rocket", "Brain", "Users", "Calendar", "ChefHat", "Dumbbell",
  "Lightbulb", "Map", "FileText", "Folder", "Star"
];

const COLOR_OPTIONS = [
  { value: "bg-blue-600", label: "Blue" },
  { value: "bg-green-600", label: "Green" },
  { value: "bg-purple-600", label: "Purple" },
  { value: "bg-orange-500", label: "Orange" },
  { value: "bg-red-600", label: "Red" },
  { value: "bg-amber-600", label: "Amber" },
  { value: "bg-teal-600", label: "Teal" },
  { value: "bg-pink-600", label: "Pink" },
  { value: "bg-slate-700", label: "Slate" },
  { value: "bg-indigo-600", label: "Indigo" },
];

export function TemplateBuilder({ open, onOpenChange, editingTemplate }: TemplateBuilderProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("basics");
  
  const [name, setName] = useState(editingTemplate?.name || "");
  const [description, setDescription] = useState(editingTemplate?.description || "");
  const [tagline, setTagline] = useState(editingTemplate?.tagline || "");
  const [category, setCategory] = useState(editingTemplate?.category || "Lifestyle");
  const [icon, setIcon] = useState(editingTemplate?.icon || "Sparkles");
  const [color, setColor] = useState(editingTemplate?.color || "bg-blue-600");
  
  const [topics, setTopics] = useState<SpaceTemplateTopicConfig[]>(
    editingTemplate?.templateData?.topics || []
  );
  const [agents, setAgents] = useState<SpaceTemplateAgentConfig[]>(
    editingTemplate?.templateData?.agents || []
  );
  const [prompts, setPrompts] = useState<SpaceTemplatePromptConfig[]>(
    editingTemplate?.templateData?.prompts || []
  );
  const [goals, setGoals] = useState<string[]>(
    editingTemplate?.templateData?.goals || []
  );
  const [newGoal, setNewGoal] = useState("");

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error("Not authenticated");
      
      const slug = name.toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
      
      const templateData: SpaceTemplateData = {
        topics,
        agents,
        prompts,
        goals,
        lens: {
          purpose: description,
          priorities: goals.slice(0, 4),
        }
      };

      return spaceTemplatesService.createTemplate({
        slug,
        name,
        description,
        tagline,
        category,
        icon,
        color,
        templateData,
        createdByUserId: user.id,
        isSystem: "false",
        isFeatured: "false",
        isPublished: "true",
        tier: "free",
      });
    },
    onSuccess: () => {
      toast({
        title: "Template Created",
        description: "Your custom template is now available.",
      });
      queryClient.invalidateQueries({ queryKey: ["space-templates"] });
      resetForm();
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create template",
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    setName("");
    setDescription("");
    setTagline("");
    setCategory("Lifestyle");
    setIcon("Sparkles");
    setColor("bg-blue-600");
    setTopics([]);
    setAgents([]);
    setPrompts([]);
    setGoals([]);
    setActiveTab("basics");
  };

  const addTopic = () => {
    setTopics([...topics, { name: "", icon: "Folder", description: "" }]);
  };

  const updateTopic = (index: number, updates: Partial<SpaceTemplateTopicConfig>) => {
    const updated = [...topics];
    updated[index] = { ...updated[index], ...updates };
    setTopics(updated);
  };

  const removeTopic = (index: number) => {
    setTopics(topics.filter((_, i) => i !== index));
  };

  const addAgent = () => {
    setAgents([...agents, { name: "", description: "", persona: "", systemPrompt: "", capabilities: [] }]);
  };

  const updateAgent = (index: number, updates: Partial<SpaceTemplateAgentConfig>) => {
    const updated = [...agents];
    updated[index] = { ...updated[index], ...updates };
    setAgents(updated);
  };

  const removeAgent = (index: number) => {
    setAgents(agents.filter((_, i) => i !== index));
  };

  const addPrompt = () => {
    setPrompts([...prompts, { name: "", description: "", template: "", category: "General", variables: [] }]);
  };

  const updatePrompt = (index: number, updates: Partial<SpaceTemplatePromptConfig>) => {
    const updated = [...prompts];
    updated[index] = { ...updated[index], ...updates };
    setPrompts(updated);
  };

  const removePrompt = (index: number) => {
    setPrompts(prompts.filter((_, i) => i !== index));
  };

  const addGoal = () => {
    if (newGoal.trim()) {
      setGoals([...goals, newGoal.trim()]);
      setNewGoal("");
    }
  };

  const removeGoal = (index: number) => {
    setGoals(goals.filter((_, i) => i !== index));
  };

  const canSave = name.trim().length > 0 && description.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            {editingTemplate ? "Edit Template" : "Create New Template"}
          </DialogTitle>
          <DialogDescription>
            Build a reusable template with topics, AI agents, and prompts
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="basics" data-testid="tab-basics">
              <Palette className="h-4 w-4 mr-1" />
              Basics
            </TabsTrigger>
            <TabsTrigger value="topics" data-testid="tab-topics">
              <FolderPlus className="h-4 w-4 mr-1" />
              Topics ({topics.length})
            </TabsTrigger>
            <TabsTrigger value="agents" data-testid="tab-agents">
              <Bot className="h-4 w-4 mr-1" />
              Agents ({agents.length})
            </TabsTrigger>
            <TabsTrigger value="prompts" data-testid="tab-prompts">
              <MessageSquare className="h-4 w-4 mr-1" />
              Prompts ({prompts.length})
            </TabsTrigger>
            <TabsTrigger value="goals" data-testid="tab-goals">
              Goals ({goals.length})
            </TabsTrigger>
          </TabsList>

          <ScrollArea className="flex-1 mt-4">
            <TabsContent value="basics" className="space-y-4 m-0">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Template Name</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., My Learning Hub"
                    data-testid="input-template-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tagline">Tagline</Label>
                  <Input
                    id="tagline"
                    value={tagline}
                    onChange={(e) => setTagline(e.target.value)}
                    placeholder="Short catchy description"
                    data-testid="input-template-tagline"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe what this template is for..."
                  className="min-h-[100px]"
                  data-testid="input-template-description"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger data-testid="select-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TEMPLATE_CATEGORIES.filter(c => c !== "All").map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Icon</Label>
                  <Select value={icon} onValueChange={setIcon}>
                    <SelectTrigger data-testid="select-icon">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ICON_OPTIONS.map(iconName => (
                        <SelectItem key={iconName} value={iconName}>{iconName}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Color</Label>
                  <Select value={color} onValueChange={setColor}>
                    <SelectTrigger data-testid="select-color">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COLOR_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          <div className="flex items-center gap-2">
                            <div className={`h-3 w-3 rounded-full ${opt.value}`} />
                            {opt.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="topics" className="space-y-4 m-0">
              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground">
                  Add topics/folders that will be created in the space
                </p>
                <Button size="sm" onClick={addTopic} data-testid="button-add-topic">
                  <Plus className="h-4 w-4 mr-1" />
                  Add Topic
                </Button>
              </div>

              {topics.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="py-8 text-center text-muted-foreground">
                    No topics yet. Click "Add Topic" to get started.
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {topics.map((topic, index) => (
                    <Card key={index}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <GripVertical className="h-5 w-5 text-muted-foreground mt-2 cursor-move" />
                          <div className="flex-1 grid grid-cols-3 gap-3">
                            <Input
                              value={topic.name}
                              onChange={(e) => updateTopic(index, { name: e.target.value })}
                              placeholder="Topic name"
                              data-testid={`input-topic-name-${index}`}
                            />
                            <Select 
                              value={topic.icon} 
                              onValueChange={(v) => updateTopic(index, { icon: v })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {ICON_OPTIONS.map(iconName => (
                                  <SelectItem key={iconName} value={iconName}>{iconName}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Input
                              value={topic.description || ""}
                              onChange={(e) => updateTopic(index, { description: e.target.value })}
                              placeholder="Description (optional)"
                            />
                          </div>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => removeTopic(index)}
                            data-testid={`button-remove-topic-${index}`}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="agents" className="space-y-4 m-0">
              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground">
                  Add AI agents with specialized personas for this template
                </p>
                <Button size="sm" onClick={addAgent} data-testid="button-add-agent">
                  <Plus className="h-4 w-4 mr-1" />
                  Add Agent
                </Button>
              </div>

              {agents.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="py-8 text-center text-muted-foreground">
                    No agents yet. Click "Add Agent" to create a specialized AI assistant.
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {agents.map((agent, index) => (
                    <Card key={index}>
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base">Agent {index + 1}</CardTitle>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => removeAgent(index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Name</Label>
                            <Input
                              value={agent.name}
                              onChange={(e) => updateAgent(index, { name: e.target.value })}
                              placeholder="e.g., Research Assistant"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Short Description</Label>
                            <Input
                              value={agent.description}
                              onChange={(e) => updateAgent(index, { description: e.target.value })}
                              placeholder="What this agent does"
                            />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Persona</Label>
                          <Textarea
                            value={agent.persona || ""}
                            onChange={(e) => updateAgent(index, { persona: e.target.value })}
                            placeholder="Describe the agent's personality and expertise..."
                            className="min-h-[60px]"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">System Prompt</Label>
                          <Textarea
                            value={agent.systemPrompt || ""}
                            onChange={(e) => updateAgent(index, { systemPrompt: e.target.value })}
                            placeholder="Instructions for how the AI should behave..."
                            className="min-h-[80px]"
                          />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="prompts" className="space-y-4 m-0">
              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground">
                  Add reusable prompt templates. Use {"{{variable}}"} for dynamic inputs.
                </p>
                <Button size="sm" onClick={addPrompt} data-testid="button-add-prompt">
                  <Plus className="h-4 w-4 mr-1" />
                  Add Prompt
                </Button>
              </div>

              {prompts.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="py-8 text-center text-muted-foreground">
                    No prompts yet. Click "Add Prompt" to create a template.
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {prompts.map((prompt, index) => (
                    <Card key={index}>
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base">Prompt {index + 1}</CardTitle>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => removePrompt(index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Name</Label>
                            <Input
                              value={prompt.name}
                              onChange={(e) => updatePrompt(index, { name: e.target.value })}
                              placeholder="e.g., Summarize Article"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Category</Label>
                            <Input
                              value={prompt.category || ""}
                              onChange={(e) => updatePrompt(index, { category: e.target.value })}
                              placeholder="e.g., Research, Writing"
                            />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Description</Label>
                          <Input
                            value={prompt.description || ""}
                            onChange={(e) => updatePrompt(index, { description: e.target.value })}
                            placeholder="What this prompt does"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Template</Label>
                          <Textarea
                            value={prompt.template}
                            onChange={(e) => updatePrompt(index, { template: e.target.value })}
                            placeholder="Enter your prompt template... Use {{variable}} for dynamic inputs."
                            className="min-h-[100px] font-mono text-sm"
                          />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="goals" className="space-y-4 m-0">
              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground">
                  Add starter goals for users of this template
                </p>
              </div>

              <div className="flex gap-2">
                <Input
                  value={newGoal}
                  onChange={(e) => setNewGoal(e.target.value)}
                  placeholder="Enter a goal..."
                  onKeyDown={(e) => e.key === "Enter" && addGoal()}
                  data-testid="input-new-goal"
                />
                <Button onClick={addGoal} disabled={!newGoal.trim()}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {goals.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="py-8 text-center text-muted-foreground">
                    No goals yet. Add some starter goals above.
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {goals.map((goal, index) => (
                    <div 
                      key={index}
                      className="flex items-center gap-2 p-3 rounded-lg bg-muted/50"
                    >
                      <div className="h-2 w-2 rounded-full bg-primary" />
                      <span className="flex-1">{goal}</span>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => removeGoal(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </ScrollArea>
        </Tabs>

        <DialogFooter className="gap-2 sm:gap-0 border-t pt-4">
          <div className="flex-1 text-sm text-muted-foreground">
            {topics.length} topics, {agents.length} agents, {prompts.length} prompts, {goals.length} goals
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={() => createMutation.mutate()}
            disabled={!canSave || createMutation.isPending}
            data-testid="button-save-template"
          >
            {createMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Template
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
